import {useLayoutEffect} from 'react'

const CSS_VAR_NAME = '--artifacts-panel-top'

function measureAppHeaderBottom(): number {
  if (typeof document === 'undefined') return 0
  const el =
    document.querySelector<HTMLElement>('.AppHeader') ?? document.querySelector<HTMLElement>('header[role="banner"]')
  if (!el) return 0
  return Math.max(0, el.getBoundingClientRect().bottom)
}

/**
 * Maintains the `--artifacts-panel-top` CSS variable on the document
 * root with the AppHeader's bottom Y (clamped to 0 once it scrolls
 * off-screen). The panel CSS uses this var for `top:` so the panel is
 * always flush below the AppHeader.
 *
 * Listeners are only attached while `enabled` (the panel is visible), so the
 * issue view incurs no scroll/resize work when no artifact is open.
 *
 * Note: the issue-viewer's sticky title header is padded via the
 * panel-reservation rules in IssueViewer.module.css so it stops at the
 * left edge of the panel and doesn't need to be accounted for here.
 */
export function useArtifactsPanelTopVar(enabled: boolean): void {
  useLayoutEffect(() => {
    if (!enabled || typeof document === 'undefined' || typeof window === 'undefined') return

    // Only write to the CSS variable when the value actually changes to avoid
    // triggering style invalidation on every scroll/resize tick.
    let lastValue = Number.NaN
    const measure = () => {
      const next = measureAppHeaderBottom()
      if (next === lastValue) return
      lastValue = next
      document.documentElement.style.setProperty(CSS_VAR_NAME, `${next}px`)
    }

    measure()
    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }
    // Header position relative to the viewport changes on scroll; neither
    // ResizeObserver nor IntersectionObserver reports the header's live bottom Y,
    // so listening to scroll (rAF-throttled) is required here. Header size
    // changes, including responsive changes, are covered by the ResizeObserver.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', schedule, {capture: true})
    const ro = new ResizeObserver(schedule)
    const appHeader = document.querySelector<HTMLElement>('.AppHeader')
    if (appHeader) ro.observe(appHeader)
    const banner = document.querySelector<HTMLElement>('header[role="banner"]')
    if (banner && banner !== appHeader) ro.observe(banner)
    return () => {
      window.removeEventListener('scroll', schedule, {capture: true})
      ro.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [enabled])
}
